import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Box,
  Stack,
  HStack,
  Button,
  IconButton,
  useToast,
} from "@chakra-ui/react";
import { ViewIcon, ViewOffIcon } from "@chakra-ui/icons";
import Form from "@rjsf/chakra-ui";
import validator from "@rjsf/validator-ajv8";
import ImageWidget from "../../CommonComponents/ImageWidget";
import ImagePreview from "../../components/ImagePreview";
import {
  createNewPost,
  getMasterlistKeywordsAutocomplete,
  getPostData,
  updatePostData,
} from "../../services/Posts";
import {
  reverseTransformData,
  transformData,
  transformOptions,
} from "../../util/helper";
import TypeaheadWidget from "../../CommonComponents/TypeaheadWidget";
import { getResearchCategoriesAPI } from "../../services/Films";
import FormLoader from "../../CommonComponents/FormLoader";
import {
  getApiErrorMessage,
  handleApiError,
  showSuccessToast,
} from "../../util/errorHandler";
import {
  exploreSearchHintOrderSchema,
  exploreSearchHintOrderUiSchema,
  exploreSearchHintKindSchema,
  exploreSearchHintKindUiSchema,
  useSearchHintPoolSize,
} from "../../constants/exploreSearchHints";

// Left Column Schema - Basic Fields
const leftColumnSchema = (alias, showLiveStartTime) => ({
  title: "Post Fields",
  description: "",
  type: "object",
  properties: {
    _id: {
      type: "string",
      title: "_id",
    },
    isVisible: {
      type: "boolean",
      title: "Is Visible",
      default: true,
    },
    featured: {
      type: "boolean",
      title: "Featured",
      default: false,
    },
    alias: {
      type: "string",
      title: "Alias",
      default: "",
    },
    ...(!!alias
      ? {
          wnn: {
            type: "string",
            title: "WNN",
            default: "",
          },
        }
      : {}),
    title: {
      type: "string",
      title: "Title",
      default: "",
    },
    title_in_hindi: {
      type: "string",
      title: "Title in Hindi",
      default: "",
    },
    description: {
      type: "string",
      title: "Description",
    },
    image: {
      type: "string",
      title: "Image",
      default: "",
    },
    preview_image: {
      type: "string",
      title: "Image",
    },
    video_link: {
      type: "string",
      title: "Video Link",
      default: "",
    },
    ...(showLiveStartTime
      ? {
          live_start_time: {
            type: "string",
            format: "date-time",
            title: "Live Start Time",
            default: "",
          },
        }
      : {}),

    insight_alias: {
      type: "string",
      title: "Insight Alias",
      default: "",
    },
    post_number: {
      type: ["integer", "null"],
      title: "Post Number",
      default: null,
    },
    canto_cover_id: {
      type: "string",
      title: "Canto Cover ID",
      default: "",
    },
    post_card_image: {
      type: "string",
      title: "Post Card Image",
      default: "",
    },
    explore_search_hint_order: exploreSearchHintOrderSchema(),
    explore_search_hint_kind: exploreSearchHintKindSchema('"Post"'),
    masterlist_alias: {
      type: "array",
      items: {
        type: "string",
      },
      title: "Masterlist Alias",
      default: [],
    },
    masterlist_tag_alias: {
      type: "array",
      items: {
        type: "string",
      },
      title: "Masterlist Tag Alias",
      default: [],
    },
  },
  required: ["alias", "title"],
});

// Right Column Schema - Tags & Metadata
const rightColumnSchema = () => ({
  title: "",
  description: "",
  type: "object",
  properties: {
    keyword_masterlist_tag_alias: {
      type: "array",
      items: {
        type: "string",
      },
      title: "Keyword Masterlist Tag Alias",
      default: [],
    },
    provisional_masterlists_tag: {
      type: "array",
      items: {
        type: "string",
      },
      title: "Provisional Masterlists Tag",
      default: [],
    },
    provisional_masterlists_tag_alias: {
      type: "array",
      items: {
        type: "string",
      },
      title: "Provisional Masterlists Tag Alias",
      default: [],
    },
    post_tags: {
      type: "array",
      items: {
        type: "string",
      },
      title: "Post Tags",
      default: [],
    },
    post_tags_alias: {
      type: "array",
      items: {
        type: "string",
      },
      title: "Post Tags Alias",
      default: [],
    },
    post_images_alias: {
      type: "array",
      title: "Post images alias",
      items: {
        type: "object",
        properties: {
          image_alias: {
            type: "string",
            title: "Image Alias",
            default: "",
          },
          image: {
            type: "string",
            title: "Image",
            default: "",
          },
        },
      },
    },
    research_category_tag_alias: {
      type: "array",
      items: {
        type: "string",
      },
      title: "Research Category Tag Alias",
      default: [],
    },
    social_media_hashtags: {
      type: "array",
      items: {
        type: "string",
      },
      title: "Social Media Hashtags",
      default: [],
    },
    show_at: {
      type: "string",
      format: "date-time",
      title: "Show At",
      default: "",
    },
  },
});

// Left Column UI Schema
const leftColumnUiSchema = (alias, isSubmitting) => ({
  "ui:submitButtonOptions": {
    norender: true,
  },
  "ui:style": {
    fontSize: "14px",
  },
  _id: {
    "ui:widget": "hidden",
  },
  alias: {
    "ui:readonly": !!alias,
    "ui:disabled": !!alias,
    "ui:emptyValue": "",
  },
  description: {
    "ui:widget": "textarea",
    "ui:emptyValue": "",
  },
  ...(alias
    ? {
        wnn: {
          "ui:emptyValue": "",
          "ui:readonly": true,
          "ui:disabled": true,
        },
      }
    : {}),
  title: {
    "ui:emptyValue": "",
  },
  title_in_hindi: {
    "ui:emptyValue": "",
  },
  insight_alias: {
    "ui:emptyValue": "",
  },
  post_number: {
    "ui:emptyValue": null,
    "ui:options": {
      inputType: "number",
    },
  },
  canto_cover_id: {
    "ui:emptyValue": "",
  },
  image: {
    "ui:emptyValue": "",
  },
  post_card_image: {
    "ui:emptyValue": "",
  },
  explore_search_hint_order: exploreSearchHintOrderUiSchema(),
  explore_search_hint_kind: exploreSearchHintKindUiSchema(),
  preview_image: {
    "ui:widget": ImageWidget,
  },
  video_link: {
    "ui:emptyValue": "",
  },
  live_start_time: {
    "ui:emptyValue": "",
  },
  masterlist_tag_alias: {
    items: {
      "ui:widget": "typeahead2",
    },
  },
});

// Right Column UI Schema
const rightColumnUiSchema = (isSubmitting) => ({
  "ui:submitButtonOptions": {
    norender: true,
  },
  "ui:style": {
    fontSize: "14px",
  },
  post_images_alias: {
    items: {
      image: {
        "ui:widget": ImageWidget,
      },
    },
  },
  research_category_tag_alias: {
    items: {
      "ui:widget": "typeahead",
    },
  },
});

// Combined Schema for Single Column (Preview Mode)
const combinedSchema = (alias, showLiveStartTime) => ({
  title: "Post Fields",
  description: "",
  type: "object",
  properties: {
    _id: {
      type: "string",
      title: "_id",
    },
    featured: {
      type: "boolean",
      title: "Featured",
      default: false,
    },
    alias: {
      type: "string",
      title: "Alias",
      default: "",
    },
    ...(!!alias
      ? {
          wnn: {
            type: "string",
            title: "WNN",
            default: "",
          },
        }
      : {}),
    title: {
      type: "string",
      title: "Title",
      default: "",
    },
    title_in_hindi: {
      type: "string",
      title: "Title in Hindi",
      default: "",
    },
    description: {
      type: "string",
      title: "Description",
    },
    image: {
      type: "string",
      title: "Image",
      default: "",
    },
    preview_image: {
      type: "string",
      title: "Image",
    },
    video_link: {
      type: "string",
      title: "Video Link",
      default: "",
    },
    ...(showLiveStartTime
      ? {
          live_start_time: {
            type: "string",
            format: "date-time",
            title: "Live Start Time",
            default: "",
          },
        }
      : {}),
    insight_alias: {
      type: "string",
      title: "Insight Alias",
      default: "",
    },
    post_number: {
      type: ["integer", "null"],
      title: "Post Number",
      default: null,
    },
    canto_cover_id: {
      type: "string",
      title: "Canto Cover ID",
      default: "",
    },
    post_card_image: {
      type: "string",
      title: "Post Card Image",
      default: "",
    },
    explore_search_hint_order: exploreSearchHintOrderSchema(),
    explore_search_hint_kind: exploreSearchHintKindSchema('"Post"'),
    masterlist_alias: {
      type: "array",
      title: "Masterlist Alias",
      items: {
        type: "string",
      },
      default: [],
    },
    masterlist_tag_alias: {
      type: "array",
      title: "Masterlist Tag Alias",
      items: {
        type: "string",
      },
      default: [],
    },
    isVisible: {
      type: "boolean",
      title: "Is Visible",
      default: true,
    },
    keyword_masterlist_tag_alias: {
      type: "array",
      title: "Keyword Masterlist Tag Alias",
      items: {
        type: "string",
      },
      default: [],
    },
    provisional_masterlists_tag: {
      type: "array",
      title: "Provisional Masterlists Tag",
      items: {
        type: "string",
      },
      default: [],
    },
    provisional_masterlists_tag_alias: {
      type: "array",
      title: "Provisional Masterlists Tag Alias",
      items: {
        type: "string",
      },
      default: [],
    },
    post_tags: {
      type: "array",
      title: "Post Tags",
      items: {
        type: "string",
      },
      default: [],
    },
    post_tags_alias: {
      type: "array",
      title: "Post Tags Alias",
      items: {
        type: "string",
      },
      default: [],
    },
    post_images_alias: {
      type: "array",
      title: "Post Images Alias",
      items: {
        type: "string",
      },
      default: [],
    },
    research_category_tag_alias: {
      type: "array",
      title: "Research Category Tag Alias",
      items: {
        type: "string",
      },
      default: [],
    },
    social_media_hashtags: {
      type: "array",
      title: "Social Media Hashtags",
      items: {
        type: "string",
      },
      default: [],
    },
    show_at: {
      type: "string",
      title: "Show At",
      default: "",
    },
  },
  required: ["alias", "title"],
});

// Combined UI Schema for Single Column (Preview Mode)
const combinedUiSchema = (alias, isSubmitting) => ({
  "ui:submitButtonOptions": {
    norender: true,
  },
  "ui:style": {
    fontSize: "14px",
  },
  _id: {
    "ui:widget": "hidden",
  },
  alias: {
    "ui:readonly": !!alias,
    "ui:disabled": !!alias,
    "ui:emptyValue": "",
  },
  description: {
    "ui:widget": "textarea",
    "ui:emptyValue": "",
  },
  ...(alias
    ? {
        wnn: {
          "ui:emptyValue": "",
          "ui:readonly": true,
          "ui:disabled": true,
        },
      }
    : {}),
  title: {
    "ui:emptyValue": "",
  },
  title_in_hindi: {
    "ui:emptyValue": "",
  },
  insight_alias: {
    "ui:emptyValue": "",
  },
  post_number: {
    "ui:emptyValue": null,
    "ui:options": {
      inputType: "number",
    },
  },
  canto_cover_id: {
    "ui:emptyValue": "",
  },
  image: {
    "ui:emptyValue": "",
  },
  post_card_image: {
    "ui:emptyValue": "",
  },
  explore_search_hint_order: exploreSearchHintOrderUiSchema(),
  explore_search_hint_kind: exploreSearchHintKindUiSchema(),
  preview_image: {
    "ui:widget": ImageWidget,
  },
  video_link: {
    "ui:emptyValue": "",
  },
  live_start_time: {
    "ui:emptyValue": "",
  },
  post_images_alias: {
    items: {
      image: {
        "ui:widget": ImageWidget,
      },
    },
  },
  masterlist_tag_alias: {
    items: {
      "ui:widget": "typeahead2",
    },
  },
  research_category_tag_alias: {
    items: {
      "ui:widget": "typeahead",
    },
  },
});

const PostDetailsForm = () => {
  const { alias } = useParams();
  const [leftFormData, setLeftFormData] = useState({});
  const [rightFormData, setRightFormData] = useState({});
  const [combinedFormData, setCombinedFormData] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [quickSave, setQuickSave] = useState(false);
  const [showImagePreview, setShowImagePreview] = useState(false);
  const toast = useToast();
  // Re-renders once the live hint pool size (app_configs) is known.
  useSearchHintPoolSize();
  const navigate = useNavigate();
  const previewEnabled = useSelector((state) => state.preview.isEnabled);

  const widgets = {
    typeahead: (props) => (
      <TypeaheadWidget
        {...props}
        fetchOptions={(inputValue) => fetchCategories(inputValue, "alias")}
      />
    ),
    typeahead1: (props) => (
      <TypeaheadWidget
        {...props}
        fetchOptions={(inputValue) => fetchCategories(inputValue, "value")}
      />
    ),
    typeahead2: (props) => (
      <TypeaheadWidget
        {...props}
        fetchOptions={(inputValue) => fetchMasterlistKeywords(inputValue)}
      />
    ),
  };

  const hasVideoLink = (formData) =>
    typeof formData?.video_link === "string" &&
    formData.video_link.trim().length > 0;

  // Split data for two-column layout
  const splitFormData = (data = {}) => {
    const leftFields = [
      "_id",
      "featured",
      "isVisible",
      "alias",
      "wnn",
      "title",
      "title_in_hindi",
      "description",
      "image",
      "post_card_image",
      "explore_search_hint_order",
      "explore_search_hint_kind",
      "video_link",
      "live_start_time",
      "preview_image",
      "insight_alias",
      "post_number",
      "canto_cover_id",
      "masterlist_alias",
      "masterlist_tag_alias",
    ];
    const rightFields = [
      "keyword_masterlist_tag_alias",
      "provisional_masterlists_tag",
      "provisional_masterlists_tag_alias",
      "post_tags",
      "post_tags_alias",
      "post_images_alias",
      "research_category_tag_alias",
      "social_media_hashtags",
      "show_at",
      "created_at",
      "updated_at",
      "__v",
    ];

    const leftData = {};
    const rightData = {};

    // Process left fields with defaults
    leftFields.forEach((field) => {
      if (data.hasOwnProperty(field)) {
        leftData[field] = data[field];
      } else {
        // Set appropriate defaults
        if (field === "featured") leftData[field] = false;
        else if (field === "isVisible") leftData[field] = true;
        else if (
          field === "post_number" ||
          field === "explore_search_hint_order"
        )
          leftData[field] = null;
        else if (
          field === "masterlist_tag_alias" ||
          field === "masterlist_alias"
        )
          leftData[field] = [];
        else leftData[field] = "";
      }
    });

    // Process right fields with defaults
    rightFields.forEach((field) => {
      if (data.hasOwnProperty(field)) {
        rightData[field] = data[field];
      } else {
        // Most right fields are arrays
        if (
          field.includes("tag") ||
          field.includes("alias") ||
          field.includes("hashtags") ||
          field === "post_images_alias"
        ) {
          rightData[field] = [];
        } else {
          rightData[field] = "";
        }
      }
    });

    return { leftData, rightData };
  };

  // Combine data from both forms
  const combineFormData = (leftData, rightData) => {
    return { ...leftData, ...rightData };
  };

  const fetchCategories = async (inputValue, keyForLabelValue = "alias") => {
    const result = await getResearchCategoriesAPI();
    const transformedOptions = transformOptions(result, keyForLabelValue);

    // This function return the options
    return transformedOptions.filter((option) =>
      option.label.toLowerCase().includes(inputValue.toLowerCase()),
    );
  };

  const dataWithImage = (data) => ({
    ...data,
    preview_image: data?.image,
  });

  const fetchMasterlistKeywords = async (inputValue) => {
    try {
      const result = await getMasterlistKeywordsAutocomplete(inputValue);
      if (result?.data && Array.isArray(result?.data)) {
        return result?.data?.map((item) => ({
          label: item?.name || item?.alias,
          value: item?.alias,
        }));
      }
      return [];
    } catch (error) {
      console.error("Error fetching masterlist keywords:", error);
      return [];
    }
  };
  // Fetch initial data
  useEffect(() => {
    const getBasicData = async () => {
      try {
        if (alias) {
          const result = await getPostData(alias);
          if (result) {
            const updatedDataWithImage = dataWithImage(result?.data?.[0]);

            const fieldsToTransform = ["post_images_alias"];

            // To Show image on form need to provide url to image field which is only used to show the image
            const transformedData = transformData(
              updatedDataWithImage,
              fieldsToTransform,
            );
            // transformData turns every null into "", but the hint slot schema is
            // ["integer", "null"] -- restore null for an unset slot.
            if (transformedData.explore_search_hint_order === "")
              transformedData.explore_search_hint_order = null;

            // Split data for two-column layout
            const { leftData, rightData } = splitFormData(transformedData);
            setLeftFormData(leftData);
            setRightFormData(rightData);
            setCombinedFormData(transformedData);
            setQuickSave(false);
          }
        } else {
          // For new posts, initialize empty form data
          const emptyData = {};
          const { leftData, rightData } = splitFormData(emptyData);
          setLeftFormData(leftData);
          setRightFormData(rightData);
          setCombinedFormData(emptyData);
        }
      } catch (error) {
        handleApiError(error, toast, {
          defaultErrorMessage: "Failed to fetch data",
        });
      } finally {
        setIsLoading(false);
      }
    };

    getBasicData();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alias, quickSave]);

  // Handle form changes for two-column layout
  const handleLeftFormChange = (data) => {
    setLeftFormData(data.formData);
  };

  const handleRightFormChange = (data) => {
    setRightFormData(data.formData);
  };

  const handleCombinedFormChange = (data) => {
    setCombinedFormData(data.formData);
  };

  // Submit updated data
  const handleSubmit = async (formData = null) => {
    // Use combined form data in preview mode, otherwise combine left and right forms
    const finalFormData = previewEnabled
      ? combinedFormData
      : combineFormData(leftFormData, rightFormData);

    // Validate required fields (only for existing posts)
    if (alias && (!finalFormData.alias || !finalFormData.title)) {
      return;
    }

    // For new posts, both alias and title are required
    if (!alias && (!finalFormData.alias || !finalFormData.title)) {
      return;
    }

    const fieldsToTransform = ["post_images_alias"];

    const updatedFormData = reverseTransformData(
      finalFormData,
      fieldsToTransform,
    );

    try {
      if (alias) {
        setIsSubmitting(true);
        const result = await updatePostData(alias, updatedFormData);
        if (result?.status === 200) {
          setQuickSave((prev) => !prev);
          showSuccessToast(toast, "Data updated successfully");
        }
      } else {
        setIsSubmitting(true);
        const result = await createNewPost(updatedFormData); // Call the create API
        if (result?.status === 201) {
          setQuickSave((prev) => !prev);
          showSuccessToast(toast, "New post created successfully");
          navigate(`/posts/${result?.data?.alias}`); // Navigate to the newly created post
        }
      }
    } catch (error) {
      handleApiError(error, toast, {
        defaultErrorMessage: getApiErrorMessage(
          error,
          "Failed to fulfill request",
        ),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box maxW="auto" pb="100px">
      {isLoading ? (
        <FormLoader />
      ) : (
        <Box>
          {previewEnabled ? (
            /* Single column layout for preview mode */
            <Box
              display={"flex"}
              flexDirection={"row"}
              alignItems={"flex-start"}
            >
              <Box
                flex={1}
                bg="white"
                border="1px solid"
                borderColor="gray.200"
                p={4}
                borderRadius="md"
              >
                <HStack justifyContent="space-between" mb={4}>
                  <Box fontSize="lg" fontWeight="bold">
                    Post Fields
                  </Box>
                  {combinedFormData?.image && (
                    <IconButton
                      aria-label={
                        showImagePreview
                          ? "Hide image preview"
                          : "Show image preview"
                      }
                      icon={showImagePreview ? <ViewOffIcon /> : <ViewIcon />}
                      size="sm"
                      colorScheme={showImagePreview ? "red" : "blue"}
                      onClick={() => setShowImagePreview((prev) => !prev)}
                      title={
                        showImagePreview
                          ? "Close image preview"
                          : "Open image preview"
                      }
                    />
                  )}
                </HStack>
                <Form
                  schema={combinedSchema(alias, hasVideoLink(combinedFormData))}
                  uiSchema={combinedUiSchema(alias, isSubmitting)}
                  formData={combinedFormData}
                  validator={validator}
                  onChange={handleCombinedFormChange}
                  widgets={widgets}
                  fontsize={"14px"}
                />
              </Box>
              {/* Third column for image preview when clicked */}
              {showImagePreview && combinedFormData?.image && (
                <HStack
                  sx={{ position: "sticky", top: "10px", marginLeft: "0px" }}
                >
                  <ImagePreview image={combinedFormData?.image} />
                </HStack>
              )}
            </Box>
          ) : (
            /* Two column layout with separate forms */
            <Box
              display={"flex"}
              flexDirection={"row"}
              alignItems={"flex-start"}
            >
              <Stack
                flexDirection="row"
                gap={4}
                alignItems="flex-start"
                flex={1}
              >
                <HStack alignItems="flex-start" flex={1}>
                  {/* Left Column */}
                  <Box
                    flex={1}
                    bg="white"
                    border="1px solid"
                    borderColor="gray.200"
                    p={4}
                    borderRadius="md"
                  >
                    <HStack justifyContent="space-between" mb={4}>
                      <Box fontSize="lg" fontWeight="bold">
                        Post Fields
                      </Box>
                      {leftFormData?.image && (
                        <IconButton
                          aria-label={
                            showImagePreview
                              ? "Hide image preview"
                              : "Show image preview"
                          }
                          icon={
                            showImagePreview ? <ViewOffIcon /> : <ViewIcon />
                          }
                          size="sm"
                          colorScheme={showImagePreview ? "red" : "blue"}
                          onClick={() => setShowImagePreview((prev) => !prev)}
                          title={
                            showImagePreview
                              ? "Close image preview"
                              : "Open image preview"
                          }
                        />
                      )}
                    </HStack>
                    <Form
                      schema={leftColumnSchema(
                        alias,
                        hasVideoLink(leftFormData),
                      )}
                      uiSchema={leftColumnUiSchema(alias, isSubmitting)}
                      formData={leftFormData}
                      validator={validator}
                      onChange={handleLeftFormChange}
                      widgets={widgets}
                      fontsize={"14px"}
                    />
                  </Box>
                  {/* Right Column */}
                  <Box
                    flex={1}
                    bg="white"
                    border="1px solid"
                    borderColor="gray.200"
                    p={4}
                    borderRadius="md"
                  >
                    <Form
                      schema={rightColumnSchema()}
                      uiSchema={rightColumnUiSchema(isSubmitting)}
                      formData={rightFormData}
                      validator={validator}
                      onChange={handleRightFormChange}
                      widgets={widgets}
                      fontsize={"14px"}
                    />
                  </Box>
                </HStack>
              </Stack>
              {/* Third column for image preview when clicked */}
              {showImagePreview && leftFormData?.image && (
                <HStack
                  sx={{ position: "sticky", top: "10px", marginLeft: "0px" }}
                >
                  <ImagePreview image={leftFormData?.image} />
                </HStack>
              )}
            </Box>
          )}
        </Box>
      )}

      {/* Fixed Save Button at Bottom */}
      <Box
        position="fixed"
        bottom={0}
        left="250px" // Account for sidebar width
        right={0}
        bg="white"
        borderTop="1px solid"
        borderColor="gray.200"
        p={4}
        zIndex={1000}
        display="flex"
      >
        <Box maxW="auto" display="flex" mx="auto">
          <Button
            colorScheme="blue"
            size="lg"
            width="auto"
            isLoading={isSubmitting}
            loadingText="Saving..."
            onClick={() => handleSubmit()}
            disabled={isSubmitting}
          >
            {isSubmitting ? "Saving..." : "Save"}
          </Button>
        </Box>
      </Box>

      {/* Add padding to prevent content from being hidden behind fixed button */}
      <Box pb="100px" />
    </Box>
  );
};

export default PostDetailsForm;
